<header class="z-40 h-14 items-center gap-12 bg-sem-surface-default px-5 lg:px-16 shadow-sm flex justify-between">
    <div class="flex items-center lg:hidden">
        <jdi-button
            appearance="link"
            [linkUnderline]="false"
            emphasis="secondary"
            [iconOnly]="true"
            icon="menu-2"
            [ariaLabel]="openMainNavAriaLabel"
            size="md"
            typography="default"
            (click)="openMobileNavDrawer()">
        </jdi-button>
    </div>

    <a
        class="inline-flex min-w-0 items-center no-underline"
        [attr.href]="localeService.buildLocalizedAbsoluteUrl(navigationLocale, '/shipments')"
        (click)="onLogoClick($event)"
        [attr.aria-label]="goToHomeAriaLabel">
        <img
            [src]="logoPath"
            alt="MyMidland"
            class="block h-6 w-37.5 object-contain min-[640px]:w-[176.37px]" />
    </a>

    <div class="flex justify-end">
        <nav class="hidden gap-12 flex-wrap justify-end lg:flex" [attr.aria-label]="primaryNavigationAriaLabel">
            @for (item of visibleNavItems; track item.href) {
                <jdi-button
                    class="app-header-nav-link font-medium"
                    [class.app-header-nav-link--active]="isActive(item)"
                    appearance="nav-link"
                    [emphasis]="isActive(item) ? 'primary' : 'secondary'"
                    size="md"
                    typography="header"
                    [label]="item.label"
                    [ariaLabel]="getOpenNavItemAriaLabel(item.label)"
                    [disabled]="isNavItemDisabled(item)"
                    (click)="!isNavItemDisabled(item) && navigate(item)" />
            }
        </nav>
    
        <div
            #userMenuContainer
            class="relative justify-self-end ms-8"
            cdkOverlayOrigin
            #userMenuOrigin="cdkOverlayOrigin">
            <jdi-user-profile-trigger
                size="40px"
                [userName]="userName"
                [defaultInitials]="avatarInitials"
                [open]="userMenuOpen"
                [popupId]="userMenuDropdownId"
                (toggleRequested)="toggleUserMenu()" />
    
            <jdi-user-menu-dropdown
                [origin]="userMenuOrigin"
                [(open)]="userMenuOpen"
                [dropdownId]="userMenuDropdownId"
                [panelAriaLabel]="userMenuPanelAriaLabel"
                [overlayOffsetX]="0"
                [overlayOffsetY]="0">
                <section user-menu-section class="flex flex-col gap-2">
                    <div user-menu-section-header class="px-2 text-xs font-semibold uppercase tracking-wide text-sem-content-muted">
                        {{ accountSectionLabel }}
                    </div>
    
                    <ul user-menu-section-content class="flex flex-col gap-1" [attr.aria-label]="accountDetailsAriaLabel">
                        @for (item of avatarInfoItems; track item.label) {
                            <li user-menu-detail-item class="flex items-center gap-2 rounded-md px-2 py-2 text-sem-content-secondary">
                                <jdi-icon [name]="item.icon" size="18"></jdi-icon>
    
                                <div class="flex min-w-0 flex-col gap-0.5">
                                    <span class="text-xs text-sem-content-muted">{{ item.label }}</span>
                                    <span class="truncate text-sm text-sem-content-primary">{{ item.value }}</span>
                                </div>
                            </li>
                        }
                    </ul>
                </section>
    
                <section user-menu-section class="flex flex-col gap-3 border-t border-sem-border-default pt-3">
                    <div user-menu-section-header class="px-2 text-xs font-semibold uppercase tracking-wide text-sem-content-muted">
                        {{ preferencesSectionLabel }}
                    </div>
    
                    <div user-menu-section-content class="flex flex-col">
                        <jdi-input-select
                            [label]="languageLabel"
                            [options]="languageOptions"
                            [value]="selectedLanguage"
                            (valueChange)="updateLanguage($event)">
                        </jdi-input-select>
    
                        <div class="flex items-center justify-between rounded-md pt-3 text-sm text-sem-content-primary">
                            <span id="header-dark-theme-label" class="flex items-center gap-2">
                                @if (themeEnabled) {
                                    <jdi-icon name="moon" size="18"></jdi-icon>
                                } @else {
                                    <jdi-icon name="sun" size="18"></jdi-icon>
                                }
                                {{ themeEnabled ? darkThemeOnLabel : darkThemeOffLabel }}
                            </span>
    
                            <button
                                type="button"
                                role="switch"
                                aria-labelledby="header-dark-theme-label"
                                [attr.aria-checked]="themeEnabled"
                                (click)="updateTheme(!themeEnabled)"
                                class="relative inline-flex rounded-full cursor-pointer focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sem-border-focus transition-colors duration-200 ease-in-out border border-sem-control-switch-border h-6 w-11"
                                [class.bg-sem-action-primary-bg]="themeEnabled"
                                [class.bg-sem-control-switch-bg]="!themeEnabled">
                                <span
                                    class="absolute left-0.5 top-1/2 -translate-y-1/2 rounded-full bg-sem-control-switch-thumb-default-bg shadow transition-transform duration-200 ease-in-out h-5 w-5"
                                    [class.translate-x-5]="themeEnabled">
                                </span>
                            </button>
                        </div>
                    </div>
                </section>
    
                <section user-menu-section class="border-t border-sem-border-default pt-2">
                    <div user-menu-section-actions class="flex w-full">
                        <jdi-button
                            appearance="nav-link"
                            leftIcon="logout"
                            emphasis="secondary"
                            size="md"
                            typography="default"
                            label="Sign Out"
                            i18n-label="@@headerSignOutLabel"
                            (click)="signOut()">
                        </jdi-button>
                    </div>
                </section>
            </jdi-user-menu-dropdown>
        </div>
    </div>

</header>

<jdi-drawer
    [ariaLabel]="mainNavigationAriaLabel"
    placement="top"
    size="md"
    [open]="mobileNavDrawerOpen"
    [closeOnBackdrop]="true"
    [closeOnEscape]="true"
    [showFooter]="false"
    variant="seamless"
    (closeRequest)="closeMobileNavDrawer()">
    <div drawer-header class="flex w-full items-center justify-between gap-3">
        <div class="flex min-w-0 items-center gap-3">
            <img
                [src]="logoPath"
                alt="MyMidland"
                class="block h-6 w-37.5 object-contain" />

            @if (enableEnvBanner) {
                <span class="rounded-full border border-brand-300 bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-900">
                    {{ environmentName }}
                </span>
            }
        </div>

        <jdi-button
            appearance="link"
            [linkUnderline]="false"
            emphasis="secondary"
            size="md"
            typography="default"
            [iconOnly]="true"
            icon="x"
            [ariaLabel]="closeMainNavAriaLabel"
            (click)="closeMobileNavDrawer()" />
    </div>

    <div drawer-body class="flex flex-col gap-6">
        <nav class="flex flex-col gap-1 items-start" aria-label="Mobile primary navigation">
            @for (item of visibleNavItems; track item.href) {
                <jdi-button
                    class="app-header-nav-link font-medium"
                    [class.app-header-nav-link--active]="isActive(item)"
                    [block]="true"
                    appearance="nav-link"
                    [emphasis]="isActive(item) ? 'primary' : 'secondary'"
                    size="md"
                    typography="default"
                    [label]="item.label"
                    [ariaLabel]="'Open ' + item.label"
                    [disabled]="isNavItemDisabled(item)"
                    (click)="!isNavItemDisabled(item) && navigateFromMobileDrawer(item)" />
            }
        </nav>
    </div>
</jdi-drawer>

@if (enableEnvBanner) {
    <div class="pointer-events-none fixed left-1/2 top-0 z-40 hidden -translate-x-1/2 rounded-b-md border border-brand-300 bg-brand-100 px-3 py-1 text-center text-xs font-medium text-brand-900 shadow-sm min-[640px]:block">
        You are in <strong>{{ environmentName }}</strong> Environment
    </div>
}